import { Product, Variant } from "@snippets/types";
import { h } from "preact";
import { useEffect, useState } from "preact/hooks";
import MiniCartPrice from "./MiniCartPrice";

interface MiniCartRecommendationsProps {
  addItem: (itemId: string) => void;
  recommendationProductHandle: string;
}

interface Metafield {
  namespace: string;
  key: string;
  value: string;
}

function MiniCartRecommendations({
  addItem,
  recommendationProductHandle,
}: MiniCartRecommendationsProps) {
  const [recommendations, setRecommendations] = useState<Product[]>([]);
  const recommendationsCount = 3;

  useEffect(() => {
    if (!recommendationProductHandle) return;

    const fetchRecommendations = async () => {
      try {
        // First try to get related products from metafields
        const metafieldProducts = await fetchMetafieldRelatedProducts();

        if (metafieldProducts && metafieldProducts.length > 0) {
          setRecommendations(metafieldProducts);
          return;
        }

        // Fall back to recommendations API if no metafield products
        await fetchRecommendationsAPI();
      } catch (e) {
        console.error("Fetch recommendations error:", e);
        setRecommendations([]);
      }
    };

    /**
     * Fetch related products from metafields
     */
    const fetchMetafieldRelatedProducts = async (): Promise<
      Product[] | null
    > => {
      try {
        // Use the product handle directly
        const productUrl = `/products/${recommendationProductHandle}.js`;
        const response = await fetch(productUrl);

        // If we get a 404, the product handle approach doesn't work, so return null to fall back to recommendations API
        if (!response.ok) {
          console.log(
            `Product URL ${productUrl} not found, falling back to recommendations API`,
          );
          return null;
        }

        const productData = await response.json();

        // Check if this product has a parent product handle
        const parentHandleMetafield = productData.metafields?.find(
          (metafield: Metafield) =>
            metafield.namespace === "brewdog" &&
            metafield.key === "parent_product_handle",
        );

        let recommendationSourceHandle = recommendationProductHandle;

        // If there's a parent product handle, use that for recommendations instead
        if (parentHandleMetafield?.value) {
          // Clean the parent handle (remove leading slash if present)
          const cleanParentHandle = parentHandleMetafield.value
            .replace(/^\/products\//, "")
            .replace(/^\//, "");
          recommendationSourceHandle = cleanParentHandle;

          // Fetch the parent product data
          const parentProductUrl = `/products/${cleanParentHandle}.js`;
          const parentResponse = await fetch(parentProductUrl);

          if (parentResponse.ok) {
            const parentProductData = await parentResponse.json();
            // Use parent product data for recommendations
            productData.metafields = parentProductData.metafields;
            console.log(
              `Using parent product ${cleanParentHandle} for recommendations instead of ${recommendationProductHandle}`,
            );
          } else {
            console.log(
              `Parent product ${cleanParentHandle} not found, using original product for recommendations`,
            );
          }
        }

        // Get AI recommendations from Search & Discovery app metafields
        const relatedProductsMetafield = productData.metafields?.find(
          (metafield: Metafield) =>
            metafield.namespace ===
              "shopify--discovery--product_recommendation" &&
            metafield.key === "related_products",
        );

        // First check for custom cart upsell products metafield
        const cartUpsellMetafield = productData.metafields?.find(
          (metafield: Metafield) =>
            metafield.namespace === "brewdog" &&
            metafield.key === "cart_upsell_products",
        );

        const selectedMetafield =
          cartUpsellMetafield || relatedProductsMetafield;

        if (!selectedMetafield?.value) {
          return null;
        }

        // Parse the metafield value which contains product IDs or handles
        let relatedProductIds;
        try {
          relatedProductIds = JSON.parse(selectedMetafield.value);
        } catch (e) {
          // If it's not JSON, treat it as a comma-separated list of handles
          relatedProductIds = selectedMetafield.value
            .split(",")
            .map((id: string) => id.trim());
        }

        if (!relatedProductIds?.length) {
          return null;
        }

        // Fetch product data for each related product
        const productPromises = relatedProductIds.map(
          async (productId: string) => {
            // Check if it's a product handle or ID
            if (productId.includes("gid://") || /^\d+$/.test(productId)) {
              // Extract the numeric ID from the product ID if it's in gid format
              const numericId = productId.replace(/\D/g, "");
              const productResponse = await fetch(`/products/${numericId}.js`);

              if (!productResponse.ok) {
                return null;
              }

              return await productResponse.json();
            } else {
              // Treat as product handle
              const productResponse = await fetch(`/products/${productId}.js`);

              if (!productResponse.ok) {
                return null;
              }

              return await productResponse.json();
            }
          },
        );

        const products = await Promise.all(productPromises);

        // Filter out null results and apply other filters
        const validProducts = products.filter((product) => product !== null);

        // If we don't have any valid products, return null to fall back to recommendations API
        if (validProducts.length === 0) {
          return null;
        }

        // Filter and limit products
        const filteredProducts = validProducts
          .filter(
            (product: Product) =>
              product.available &&
              product.title !== "Pfand" &&
              !isProductSeoHidden(product),
          )
          .slice(0, recommendationsCount);

        return filteredProducts;
      } catch (error) {
        console.error("Error fetching metafield related products:", error);
        return null;
      }
    };

    /**
     * Fall back to recommendations API
     */
    const fetchRecommendationsAPI = async (): Promise<void> => {
      const productRecommendationsUrl =
        window.themeVars.routes.product_recommendations_url;

      // We need to get the product ID for the recommendations API
      // Since we have the handle, we need to fetch the product first to get its ID
      if (!recommendationProductHandle) return;

      try {
        const productResponse = await fetch(
          `/products/${recommendationProductHandle}.js`,
        );
        if (!productResponse.ok) {
          console.error("Could not fetch product for recommendations API");
          setRecommendations([]);
          return;
        }

        const productData = await productResponse.json();
        const productId = productData.id;

        const url = `${productRecommendationsUrl}.json?product_id=${productId}&limit=20&intent=related`;

        const response = await fetch(url);
        const data = await response.json();

        const limitProducts = data.products
          .filter(
            (product: Product) =>
              product.available &&
              product.title !== "Pfand" &&
              !isProductSeoHidden(product),
          )
          .slice(0, recommendationsCount);

        setRecommendations(limitProducts);
      } catch (e) {
        console.error("Fetch recommendations error:", e);
        setRecommendations([]);
      }
    };

    fetchRecommendations();
  }, [recommendationProductHandle]);

  /**
   * Check if a product should be hidden based on seo-hidden tag or metafield
   */
  const isProductSeoHidden = (product: Product): boolean => {
    // Check for seo-hidden tag
    if (product.tags && product.tags.includes("seo-hidden")) {
      return true;
    }

    // Check for seoHidden property (from metafields)
    if (product.seoHidden === true || product.seoHidden === "1") {
      return true;
    }

    return false;
  };

  if (!recommendationProductHandle || !recommendations.length) return null;

  const filteredRecommendations = recommendations.filter(
    (product: Product) => product.type !== "Bundle",
  );

  // Add event listener for notify me functionality
  useEffect(() => {
    filteredRecommendations.forEach((product: Product) => {
      const isOutOfStock =
        !product.available &&
        product.tags?.includes("single-variant-out-of-stock");
      const hasBisEnabled =
        isOutOfStock && product.tags?.includes("back-in-stock");

      if (hasBisEnabled) {
        const bisButton = document.querySelector(
          `[data-bis-trigger][data-product="${product.id}"]`,
        ) as HTMLElement;

        const bisPopup = document.querySelector(
          `uwp-product-notify-me[data-product-id="${product.id}"]`,
        ) as HTMLElement;

        if (bisButton && bisPopup) {
          const handleBisClick = () => {
            bisPopup.setAttribute("is-visible", "true");
          };

          bisButton.addEventListener("click", handleBisClick);

          return () => {
            bisButton.removeEventListener("click", handleBisClick);
          };
        }
      }
    });
  }, [filteredRecommendations]);

  return (
    <div className="cart-upsell">
      {filteredRecommendations.length !== 0 && (
        <div>
          <p className="cart-upsell__title heading-4">
            {window.themeVars.strings.cart.loadUpYourBasket}
          </p>

          <div className="cart-upsell__product-wrapper">
            {filteredRecommendations.map((product: Product) => {
              const firstAvailableVariant = product.variants.find(
                (variant: Variant) => variant.available,
              );

              if (!firstAvailableVariant) {
                return null;
              }

              const isOutOfStock =
                !product.available &&
                product.tags?.includes("single-variant-out-of-stock");
              const hasBisEnabled =
                isOutOfStock && product.tags?.includes("back-in-stock");

              return (
                <div
                  className={`cart-upsell__product ${isOutOfStock ? "cart-upsell__product--single-variant-out-of-stock" : ""}`}
                  key={product.id}
                >
                  <img
                    src={product.images[0]}
                    alt={product.title}
                    className={`cart-upsell__product-image ${isOutOfStock ? "cart-upsell__product-image--out-of-stock" : ""}`}
                    width="80"
                    height="80"
                  />

                  <div className="cart-upsell__product-details">
                    {isOutOfStock && (
                      <span className="cart-upsell__badge badge badge-yellow">
                        {window.themeVars.strings.product.soldOut}
                      </span>
                    )}

                    <p className="cart-upsell__product-title heading-4">
                      {product.title}
                    </p>

                    {firstAvailableVariant.title !== "Default Title" && (
                      <p className="cart-upsell__product-option text-md-body">
                        {firstAvailableVariant.title}
                      </p>
                    )}

                    <MiniCartPrice
                      price={firstAvailableVariant.price}
                      compareAtPrice={firstAvailableVariant.compare_at_price}
                    />
                  </div>

                  {isOutOfStock ? (
                    hasBisEnabled ? (
                      <button
                        className="cart-upsell__product-cta btn btn-white btn-white--outline"
                        data-bis-trigger
                        data-product={product.id}
                        data-variant={firstAvailableVariant.id}
                      >
                        {window.themeVars.strings.product.notifyMe}
                      </button>
                    ) : (
                      <button
                        className="cart-upsell__product-cta btn btn-white btn-white--outline"
                        disabled
                      >
                        {window.themeVars.strings.product.soldOut}
                      </button>
                    )
                  ) : (
                    <button
                      className="cart-upsell__product-cta"
                      onClick={() => addItem(firstAvailableVariant.id)}
                      dangerouslySetInnerHTML={{
                        __html: window.themeVars.icons.addToCart,
                      }}
                    ></button>
                  )}

                  {/* Render notify me modal for products that have BIS enabled */}
                  {hasBisEnabled && (
                    <uwp-product-notify-me
                      data-product-id={product.id}
                      data-variant-id=""
                      product-data={JSON.stringify(product)}
                      is-visible="false"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default MiniCartRecommendations;
